import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { my_routes } from "../../router/all_routes";

import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { setLogin } from "../../../core/data/redux/authSlice";
import { usePermissions } from "../../../core/common/context/PermissionsUserContext";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { getUserPermissionsLabel } from "../../../api/permissions";
import { login } from "../../../api/auth";
import { formatPermissions, generateDeviceID } from "../../../helper/auth";
import { AppDispatch } from "../../../core/data/redux/store";

import { toast } from "sonner";
import { resetSessionHandling } from "../../../helper/sessionManager";
import { resetSessionExpired } from "../../../core/data/redux/sessionSlice";
import { getLocalStorage } from "../../../helper/localStorage";
// 1. définition du shéma Zod
const loginShema = z.object({
  user: z
    .string()
    .nonempty({ message: "L'email ou le matricule est requis" })
    .refine(
      (value) => {
        // console.log("Email ou matricule: ", value);
        // Vérifie si c'est un email valide
        const isEmail = z.string().email().safeParse(value).success;

        // Vérifie si c'est un matricule valide
        // Exemple : uniquement chiffres et lettres, min 4 caractères
        const isMatricule =
          /^[A-Za-z0-9-]{4,}$/.test(value) && value.includes("-");

        return isEmail || isMatricule;
      },
      {
        message:
          "Veuillez entrer une adresse e-mail valide ou un matricule valide",
      },
    ),
  password: z.string().min(8, {
    message: "Le mot de passe doit comporter au moins 8 caractères",
  }),
  rememberme: z.boolean().optional(),
});

//2.Inference de type des données du formulaire
type AuthFormData = z.infer<typeof loginShema>;

const Login: React.FC = () => {
  const routes = my_routes;

  const loginBackgrounds = [
    "/assets/img/background_1.jpeg",
    "/assets/img/background_2.jpeg",
    "/assets/img/background_3.jpeg",
    "/assets/img/background_4.jpeg",
  ];

  const [loginBackground] = useState(() => {
    const randomIndex = Math.floor(Math.random() * loginBackgrounds.length);
    return loginBackgrounds[randomIndex];
  });

  // Hooks pour la gestion de la connexion
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const userType = useSelector((state: any) => state.auth.userType);
  const sessionExpired = useSelector((state: any) => state.session.expired);

  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const { setPermissions } = usePermissions();

  // Destructuration du shema pour la gestion du formulaire avec useForm
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<AuthFormData>({
    resolver: zodResolver(loginShema),
    defaultValues: {
      user: "",
      password: "",
      rememberme: false,
    },
  });

  // fonction de soumission du formulaire de connexion
  const onSubmit = async (data: AuthFormData) => {
    try {
      const { user, password } = data;

      let device_id = getLocalStorage("deviceID");
      if (!device_id) {
        device_id = generateDeviceID();
      }

      const response = await login({
        username: user,
        password,
        platform: "WEB",
        device_id,
      });

      /** à enlever après la gestion des permissions */
      const userData = response.data.user;
      if (userData && response) {
        // récupération de ces permissions
        if (response.data.userType === "UserProfil") {
          const resp_perm = await getUserPermissionsLabel();

          // console.log(resp_perm.data.permissionsByFunctionality)
          const permissions = resp_perm.data.permissionsByFunctionality;

          // Formatter les permissions
          const formattedPermissions = formatPermissions(permissions);
          // console.log("formattedPermissions: ", formattedPermissions);
          setPermissions(formattedPermissions);
        }

        dispatch(
          setLogin({
            user: response?.data?.user,
            userType: response?.data?.userType,
          }),
        );
      }
      dispatch(resetSessionExpired());
      resetSessionHandling();

      // navigate(routes.adminDashboard);
    } catch (e: any) {
      // console.log("Erreur: ", e);
      setError("root", {
        message:
          e.response?.data?.error ||
          e.response?.data?.message ||
          e.error ||
          e.message ||
          "Une erreur est survenu lors  de la connexion veuillez essayer plus tard.",
      });
      toast.error(
        e.response?.data?.error ||
          e.response?.data?.message ||
          e.error ||
          e.message ||
          "Une erreur est survenu lors  de la connexion veuillez essayer plus tard.",
      );
    }
  };

  useEffect(() => {
    // console.log("Session Expired: ", sessionExpired);
    if (!userType || sessionExpired) return;

    // redirecton en fonction du profil utilisateur
    switch (userType) {
      case "UserProfil":
        navigate(routes.adminDashboard);
        break;
      case "StudentProfil":
        navigate(routes.studentDashboard);
        break;
      case "ParentProfil":
        navigate(routes.parentDashboard);
        break;
      case "TeacherProfil":
        navigate(routes.teacherDashboard);
        break;
      default:
        break;
    }
  }, [userType, dispatch, sessionExpired]);

  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };
  useEffect(() => {
    localStorage.setItem("menuOpened", "Dashboard");
  }, []);

  return (
    <div className="container-fuild">
      <div
        className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100"
        style={{
          backgroundImage: `url(${loginBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="row"
          style={{
            backdropFilter: "blur(2.5px)",
          }}
        >
          <div className="col-lg-12 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap ">
              <div className="col-md-4 mx-auto p-4">
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <div>
                    <div
                      className="card shadow"
                      style={{ backgroundColor: "rgba(255,255,255,0.90)" }}
                    >
                      <div className="card-body pb-3">
                        <img
                          src="/assets/img/authentication/Logo-Campus-removebg.png"
                          style={{ background: "transparent" }}
                          alt="Logo"
                        />
                        {/* header */}
                        <div className=" mb-4">
                          <h2 className="mb-2">Bienvenue</h2>
                          <p className="mb-0">
                            {/* Please enter your details to sign in */}
                            Entrez vos coordonnées pour vous connecter
                          </p>
                        </div>
                        {/* header */}
                        {errors.root && (
                          <div className="alert alert-danger" role="alert">
                            {errors.root.message}
                          </div>
                        )}
                        {/* Champs saisis */}
                        <div className="mb-3 ">
                          <label className="form-label" htmlFor="email">
                            Nom d'utilisateur
                          </label>
                          <div className="input-icon mb-3 position-relative">
                            <span className="input-icon-addon">
                              <i className="ti ti-mail" />
                            </span>
                            <input
                              id="email"
                              type="email"
                              defaultValue=""
                              className="form-control input"
                              {...register("user")}
                            />
                            {errors.user && (
                              <span className="text-danger">
                                {errors.user.message}
                              </span>
                            )}
                          </div>
                          <label className="form-label" htmlFor="password">
                            Mot de passe
                          </label>
                          <div className="pass-group">
                            <input
                              id="password"
                              type={isPasswordVisible ? "text" : "password"}
                              className="pass-input form-control input"
                              {...register("password")}
                            />
                            <span
                              className={`ti toggle-password ${
                                isPasswordVisible ? "ti-eye" : "ti-eye-off"
                              }`}
                              onClick={togglePasswordVisibility}
                            />
                          </div>
                          {errors.password && (
                            <span className="text-danger">
                              {errors.password.message}
                            </span>
                          )}
                        </div>
                        {/* /champs saisis */}
                        <div className="form-wrap form-wrap-checkbox">
                          <div className="text-end ">
                            <Link
                              to={routes.forgotPassword}
                              className="link-danger"
                            >
                              Mot de passe oublié ?
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 pt-0">
                        <div className="mb-3">
                          {!isSubmitting ? (
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="btn btn-primary w-100"
                            >
                              Se connecter
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary-light w-100"
                              type="button"
                              disabled={true}
                            >
                              <span
                                className="spinner-border spinner-border-sm align-middle me-1"
                                role="status"
                                aria-hidden="true"
                              />
                              Loading...
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
